import React from 'react';
import { DollarSign, CheckCircle2, AlertOctagon, Search, ShieldCheck, Clock } from 'lucide-react';

/**
 * Visual interactive timeline for the SOIA (SAT) pedimento lifecycle.
 *
 * Reads the structured fields persisted by `services/soia_runner.py`:
 *   pagado, pagado_fecha, seleccion_automatizada, seleccion_resultado,
 *   seleccion_fecha, reconocimiento_inicio, reconocimiento_inicio_fecha,
 *   reconocimiento_resultado, reconocimiento_resultado_fecha,
 *   desaduanado, desaduanado_fecha.
 *
 * The reconocimiento step is only rendered when the modulation is Rojo
 * (because Verde skips physical inspection and goes straight to Cumplido).
 *
 * Each step is one of: 'done' | 'active' | 'pending'.
 *   - done    → green/colored icon + opaque text + date shown
 *   - active  → pulsing ring (current stage)
 *   - pending → grayscale icon + dashed connector
 */
export function SoiaTimeline({ soiaData, lastCheck }) {
  if (!soiaData || (!soiaData.pagado && !soiaData.seleccion_automatizada && !soiaData.desaduanado)) {
    return null;
  }

  const isRojo = (soiaData.seleccion_resultado || '').toUpperCase().includes('ROJO');
  const isVerde = (soiaData.seleccion_resultado || '').toUpperCase().includes('VERDE');

  // If the pedimento is already DESANUADIZADO but earlier steps weren't
  // captured by the SAT summary view (the parser only sees "CUMPLIDO"),
  // mark them as implicitly done. We don't have dates for these, but
  // logically the pedimento couldn't have been cleared without paying and
  // being selected first.
  const desanuadizado = !!soiaData.desaduanado;
  const pagadoDone = !!soiaData.pagado || desanuadizado;
  const seleccionDone = !!soiaData.seleccion_automatizada || desanuadizado;

  const baseSteps = [
    {
      key: 'pagado',
      label: 'Pagado',
      icon: DollarSign,
      done: pagadoDone,
      // Show as muted (implicit) when we know it happened but lack the timestamp.
      implicit: pagadoDone && !soiaData.pagado,
      date: soiaData.pagado_fecha,
      color: 'indigo',
    },
    {
      key: 'seleccion',
      label: isRojo ? 'Semáforo Rojo' : isVerde ? 'Semáforo Verde' : 'Selección',
      icon: isRojo ? AlertOctagon : CheckCircle2,
      done: seleccionDone,
      implicit: seleccionDone && !soiaData.seleccion_automatizada,
      date: soiaData.seleccion_fecha,
      color: isRojo ? 'red' : 'green',
      sublabel: isRojo ? 'Reconocimiento aduanero' : isVerde ? 'Desaduanamiento libre' : null,
    },
  ];

  // Reconocimiento is only relevant for ROJO modulation.
  if (isRojo) {
    baseSteps.push({
      key: 'reconocimiento',
      label: 'Reconocimiento',
      icon: Search,
      done: !!soiaData.reconocimiento_resultado,
      // If only "inicio" is set, treat as active (in-progress)
      active: !!soiaData.reconocimiento_inicio && !soiaData.reconocimiento_resultado,
      date: soiaData.reconocimiento_resultado_fecha || soiaData.reconocimiento_inicio_fecha,
      color: 'amber',
      sublabel: soiaData.reconocimiento_resultado
        ? truncate(soiaData.reconocimiento_resultado, 60)
        : soiaData.reconocimiento_inicio
          ? 'En progreso…'
          : null,
    });
  }

  baseSteps.push({
    key: 'desaduanado',
    label: 'DESANUADIZADO',
    icon: ShieldCheck,
    done: !!soiaData.desaduanado,
    date: soiaData.desaduanado_fecha,
    color: 'cyan',
    sublabel: soiaData.desaduanado ? 'Liberación concluida' : 'Pendiente',
  });

  // Mark the first non-done step (after a done one) as the active stage,
  // unless an explicit active flag was set above.
  let foundActive = false;
  for (let i = 0; i < baseSteps.length; i++) {
    const s = baseSteps[i];
    if (s.active) { foundActive = true; continue; }
    if (!s.done && !foundActive && i > 0 && baseSteps[i - 1].done) {
      s.active = true;
      foundActive = true;
    }
  }

  return (
    <div className="mt-3 ml-8 rounded-lg border border-blue-500/20 bg-blue-500/5 p-3" data-testid="soia-timeline">
      <div className="flex items-center gap-1.5 mb-3">
        <Clock size={11} className="text-blue-400" />
        <span className="text-xs font-medium text-blue-300">Timeline SOIA (SAT)</span>
        {lastCheck && (
          <span className="text-[10px] text-muted-foreground ml-auto">
            Última consulta: {formatRelativeOrDate(lastCheck)}
          </span>
        )}
      </div>

      <div className="flex items-stretch gap-0 overflow-x-auto pb-1">
        {baseSteps.map((step, idx) => (
          <SoiaStep
            key={step.key}
            step={step}
            isLast={idx === baseSteps.length - 1}
            previousDone={idx === 0 || baseSteps[idx - 1].done}
          />
        ))}
      </div>
    </div>
  );
}

function SoiaStep({ step, isLast, previousDone }) {
  const Icon = step.icon;
  const status = step.done ? 'done' : step.active ? 'active' : 'pending';

  const colorMap = {
    indigo: { bg: 'bg-indigo-500', ring: 'ring-indigo-400/30', text: 'text-indigo-300' },
    green: { bg: 'bg-emerald-500', ring: 'ring-emerald-400/30', text: 'text-emerald-300' },
    red: { bg: 'bg-red-500', ring: 'ring-red-400/30', text: 'text-red-300' },
    amber: { bg: 'bg-amber-500', ring: 'ring-amber-400/30', text: 'text-amber-300' },
    cyan: { bg: 'bg-cyan-500', ring: 'ring-cyan-400/30', text: 'text-cyan-300' },
  };
  const c = colorMap[step.color] || colorMap.indigo;

  const circleClass =
    status === 'done'
      ? step.implicit
        ? `${c.bg} text-white opacity-60`
        : `${c.bg} text-white`
      : status === 'active'
        ? `${c.bg} text-white ring-4 ${c.ring} animate-pulse`
        : 'bg-secondary text-muted-foreground border border-border';

  const labelClass =
    status === 'pending'
      ? 'text-muted-foreground'
      : step.implicit
        ? 'text-muted-foreground'
        : c.text;
  const connectorClass = previousDone && status !== 'pending' ? c.bg : 'bg-border';

  return (
    <div className="flex-1 min-w-[110px] flex flex-col items-center relative" data-testid={`soia-step-${step.key}`}>
      {/* Connector line (from previous step) */}
      {!isLast && (
        <div className="absolute top-4 left-1/2 w-full h-0.5 -z-0">
          <div className={`h-full ${connectorClass} ${status === 'pending' ? 'opacity-40' : ''}`} />
        </div>
      )}

      {/* Circle */}
      <div
        className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${circleClass} transition-all`}
        title={step.label}
      >
        <Icon size={14} />
      </div>

      {/* Label + sublabel + date */}
      <div className="mt-1.5 text-center px-1">
        <div className={`text-[11px] font-semibold ${labelClass} leading-tight`}>{step.label}</div>
        {step.sublabel && (
          <div className="text-[9px] text-muted-foreground mt-0.5 leading-tight">{step.sublabel}</div>
        )}
        {step.date && (
          <div className="text-[9px] font-mono text-muted-foreground/80 mt-0.5">{step.date}</div>
        )}
      </div>
    </div>
  );
}

// --- helpers ---
function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n).trim() + '…' : s;
}

function formatRelativeOrDate(iso) {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffH = diffMs / 36e5;
    if (diffH < 1) return `hace ${Math.max(1, Math.round(diffMs / 6e4))} min`;
    if (diffH < 24) return `hace ${Math.round(diffH)} h`;
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(iso).slice(0, 16);
  }
}
